export type ChamberPoint3 = [number, number, number];

const ZERO_POINT: ChamberPoint3 = [0, 0, 0];
export const CHAMBER_VIEW_DIRECTION_BACKGROUND_FOCAL_POINT_KEY =
  'directionBackgroundFocalPoint';
export const CHAMBER_VIEW_DIRECTION_BACKGROUND_SERIES_ID_KEY =
  'directionBackgroundSeriesId';

export type ChamberViewBackgroundMetadata = {
  [CHAMBER_VIEW_DIRECTION_BACKGROUND_FOCAL_POINT_KEY]?: ChamberPoint3;
  [CHAMBER_VIEW_DIRECTION_BACKGROUND_SERIES_ID_KEY]?: string;
};

type ResolveChamberReentryStateParams = {
  existingPosition: ChamberPoint3 | undefined;
  existingGuideDelta: ChamberPoint3 | undefined;
  existingDirectionBackgroundFocalPoint: ChamberPoint3 | undefined;
  existingDirectionBackgroundSeriesId: string | undefined;
  currentSeriesId: string | undefined;
  canonicalFocalPoint: ChamberPoint3;
};

type ResolvedChamberReentryState = {
  position: ChamberPoint3;
  guideDelta: ChamberPoint3 | undefined;
  directionBackgroundFocalPoint: ChamberPoint3;
};

type ResolveNextDirectionBackgroundFocalPointParams = {
  currentDirectionBackgroundFocalPoint: ChamberPoint3 | undefined;
  fallbackPosition: ChamberPoint3;
  movement: ChamberPoint3;
  moveDirectionBackground: boolean;
};

type ResolveNextChamberGuidePanStateParams = {
  currentPosition: ChamberPoint3;
  currentGuideDelta: ChamberPoint3 | undefined;
  currentDirectionBackgroundFocalPoint: ChamberPoint3 | undefined;
  currentDirectionBackgroundMovement: ChamberPoint3 | undefined;
  movement: ChamberPoint3;
  isRef: boolean;
};

type ResolvedChamberGuidePanState = {
  position: ChamberPoint3;
  guideDelta: ChamberPoint3;
  directionBackgroundFocalPoint: ChamberPoint3;
  guideMovement: ChamberPoint3;
  directionBackgroundMovement: ChamberPoint3;
};

export type ChamberGuidePan = {
  guideDelta: ChamberPoint3;
  directionBackgroundMovement: ChamberPoint3;
};

type ResolveChamberGuidePanFromSeriesParams = {
  canonicalFocalPoint: ChamberPoint3;
  guidePan: ChamberGuidePan | undefined;
};

type ResolvedSeriesChamberGuidePan = ChamberGuidePan & {
  position: ChamberPoint3;
  directionBackgroundFocalPoint: ChamberPoint3;
};

function copyPoint(point: ChamberPoint3): ChamberPoint3 {
  return [point[0], point[1], point[2]];
}

function copyOptionalPoint(point: ChamberPoint3 | undefined) {
  return point ? copyPoint(point) : undefined;
}

function addPoints(left: ChamberPoint3, right: ChamberPoint3): ChamberPoint3 {
  return [left[0] + right[0], left[1] + right[1], left[2] + right[2]];
}

function subtractPoints(
  left: ChamberPoint3,
  right: ChamberPoint3,
): ChamberPoint3 {
  return [left[0] - right[0], left[1] - right[1], left[2] - right[2]];
}

export function getChamberViewDirectionBackgroundFocalPoint(
  chamberViewProps: ChamberViewBackgroundMetadata | undefined,
  fallbackPosition?: ChamberPoint3,
) {
  return copyOptionalPoint(
    chamberViewProps?.[CHAMBER_VIEW_DIRECTION_BACKGROUND_FOCAL_POINT_KEY] ??
      fallbackPosition,
  );
}

export function getChamberViewDirectionBackgroundSeriesId(
  chamberViewProps: ChamberViewBackgroundMetadata | undefined,
) {
  return chamberViewProps?.[CHAMBER_VIEW_DIRECTION_BACKGROUND_SERIES_ID_KEY];
}

export function withChamberViewDirectionBackgroundFocalPoint<T extends object>(
  chamberViewProps: T,
  directionBackgroundFocalPoint: ChamberPoint3 | undefined,
  directionBackgroundSeriesId?: string,
): T & ChamberViewBackgroundMetadata {
  if (!directionBackgroundFocalPoint) return { ...chamberViewProps };

  return {
    ...chamberViewProps,
    [CHAMBER_VIEW_DIRECTION_BACKGROUND_FOCAL_POINT_KEY]: copyPoint(
      directionBackgroundFocalPoint,
    ),
    ...(directionBackgroundSeriesId
      ? {
          [CHAMBER_VIEW_DIRECTION_BACKGROUND_SERIES_ID_KEY]:
            directionBackgroundSeriesId,
        }
      : {}),
  };
}

export function getChamberGuidePosition(
  directionBackgroundFocalPoint: ChamberPoint3,
  guideDelta: ChamberPoint3 | undefined,
): ChamberPoint3 {
  return addPoints(directionBackgroundFocalPoint, guideDelta ?? ZERO_POINT);
}

export function getChamberGuideCaptureOrigin(
  position: ChamberPoint3,
  guideDelta: ChamberPoint3 | undefined,
): ChamberPoint3 {
  return subtractPoints(position, guideDelta ?? ZERO_POINT);
}

export function getZeroChamberGuidePan(): ChamberGuidePan {
  return {
    guideDelta: copyPoint(ZERO_POINT),
    directionBackgroundMovement: copyPoint(ZERO_POINT),
  };
}

export function resolveChamberGuidePanFromSeries({
  canonicalFocalPoint,
  guidePan,
}: ResolveChamberGuidePanFromSeriesParams): ResolvedSeriesChamberGuidePan {
  const resolvedGuidePan = guidePan
    ? {
        guideDelta: copyPoint(guidePan.guideDelta),
        directionBackgroundMovement: copyPoint(
          guidePan.directionBackgroundMovement,
        ),
      }
    : getZeroChamberGuidePan();
  const directionBackgroundFocalPoint = addPoints(
    canonicalFocalPoint,
    resolvedGuidePan.directionBackgroundMovement,
  );

  return {
    ...resolvedGuidePan,
    directionBackgroundFocalPoint,
    position: getChamberGuidePosition(
      directionBackgroundFocalPoint,
      resolvedGuidePan.guideDelta,
    ),
  };
}

export function resolveNextDirectionBackgroundFocalPoint({
  currentDirectionBackgroundFocalPoint,
  fallbackPosition,
  movement,
  moveDirectionBackground,
}: ResolveNextDirectionBackgroundFocalPointParams): ChamberPoint3 {
  const backgroundFocalPoint =
    currentDirectionBackgroundFocalPoint ?? fallbackPosition;

  if (!moveDirectionBackground) return copyPoint(backgroundFocalPoint);

  return addPoints(backgroundFocalPoint, movement);
}

export function resolveNextChamberGuidePanState({
  currentPosition,
  currentGuideDelta,
  currentDirectionBackgroundFocalPoint,
  currentDirectionBackgroundMovement,
  movement,
  isRef,
}: ResolveNextChamberGuidePanStateParams): ResolvedChamberGuidePanState {
  const guideDelta = currentGuideDelta
    ? copyPoint(currentGuideDelta)
    : copyPoint(ZERO_POINT);
  const directionBackgroundFocalPoint =
    currentDirectionBackgroundFocalPoint ??
    getChamberGuideCaptureOrigin(currentPosition, guideDelta);
  const directionBackgroundMovement = currentDirectionBackgroundMovement
    ? copyPoint(currentDirectionBackgroundMovement)
    : copyPoint(ZERO_POINT);
  const position = addPoints(currentPosition, movement);

  if (isRef) {
    const nextGuideDelta = addPoints(guideDelta, movement);
    return {
      position,
      guideDelta: nextGuideDelta,
      directionBackgroundFocalPoint: copyPoint(directionBackgroundFocalPoint),
      guideMovement: nextGuideDelta,
      directionBackgroundMovement,
    };
  }

  const nextDirectionBackgroundFocalPoint = addPoints(
    directionBackgroundFocalPoint,
    movement,
  );
  const nextDirectionBackgroundMovement = addPoints(
    directionBackgroundMovement,
    movement,
  );

  return {
    position,
    guideDelta,
    directionBackgroundFocalPoint: nextDirectionBackgroundFocalPoint,
    guideMovement: guideDelta,
    directionBackgroundMovement: nextDirectionBackgroundMovement,
  };
}

export function resolveChamberReentryState({
  existingPosition,
  existingGuideDelta,
  existingDirectionBackgroundFocalPoint,
  existingDirectionBackgroundSeriesId,
  currentSeriesId,
  canonicalFocalPoint,
}: ResolveChamberReentryStateParams): ResolvedChamberReentryState {
  const guideDelta = copyOptionalPoint(existingGuideDelta);
  const canReuseDirectionBackground =
    !!existingDirectionBackgroundFocalPoint &&
    (!currentSeriesId ||
      existingDirectionBackgroundSeriesId === currentSeriesId);
  const directionBackgroundFocalPoint = copyPoint(
    canReuseDirectionBackground
      ? existingDirectionBackgroundFocalPoint
      : canonicalFocalPoint,
  );

  return {
    position: copyPoint(
      canReuseDirectionBackground && existingPosition
        ? existingPosition
        : getChamberGuidePosition(directionBackgroundFocalPoint, guideDelta),
    ),
    guideDelta,
    directionBackgroundFocalPoint,
  };
}
